import {
  Component,
  OnInit,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  OnboardingConfig,
  OnboardingConfigService,
} from '@app/onboarding/services/onboarding-config.service';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { FooterButtonStates } from '@app/onboarding/components/onboarding-footer/onboarding-footer.model';
import { TranslateService } from '@ngx-translate/core';
import { OnboardingStep } from '@app/onboarding/onboarding.enums';
import { OnboardingStateService } from '@app/onboarding/services/onboarding-state.service';
import { Skill } from '@app/onboarding/onboarding.model';

/**
 * Skills  component
 * Onboarding additional skills page, displayed on route change
 *
 * @example
 * ```
 * <dgx-skills>
 * </dgx-skills>
 * ```
 */
@Component({
  selector: 'dgx-skills',
  templateUrl: './skills.component.html',
  styleUrls: ['./skills.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SkillsComponent extends SubscriberBaseDirective implements OnInit {
  public templatePrefix: string;
  public isLoading: boolean = true;
  private footerButtonStates: FooterButtonStates;

  public i18n = this.translateService.instant([
    'OnboardingFooter_PreviousStep',
    'OnboardingFooter_NextStep',
    'Onboarding_AdditionalSkillsTitle',
    'Onboarding_AdditionalSkillsDescription',
    'Onboarding_SearchSkills_AddSkills',
  ]);

  constructor(
    private onboardingConfigService: OnboardingConfigService,
    private onboardingStateService: OnboardingStateService,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.footerButtonStates = {
      currentStep: OnboardingStep.skills,
      disableNextStep: true,
      previousButton: this.i18n.OnboardingFooter_PreviousStep,
      nextButton: this.i18n.OnboardingFooter_NextStep,
    };

    this.onboardingConfigService.footerButtonStateChange(
      this.footerButtonStates
    );
    this.onboardingConfigService.config.subscribe(
      (config: OnboardingConfig) => {
        this.isLoading = false;
        this.templatePrefix = config.templatePrefix;
        let steps;
        if (this.onboardingStateService.trackerSteps) {
          steps = this.onboardingConfigService.updateActiveTrackerStep(
            this.onboardingStateService.trackerSteps,
            OnboardingStep.skills
          );
          this.onboardingStateService.updateTrackerStep(steps);
        } else {
          this.onboardingStateService.updateTrackerStep(
            this.onboardingConfigService.getTrackerSteps(
              this.templatePrefix,
              OnboardingStep.skills,
              false
            )
          );
        }
        this.cdr.detectChanges();
      }
    );
    this.onboardingStateService.currentStepChange(OnboardingStep.skills);
  }

  /**
   * On user action selecting/deselection skills
   * trigger button state change, and tracker state change
   *
   * @param skillsSelected - true if skills are selected
   */
  public onSkillSelected(skillsSelected: boolean) {
    this.onboardingConfigService.footerButtonStateChange({
      ...this.footerButtonStates,
      disableNextStep: !skillsSelected,
    });
    this.cdr.detectChanges();
  }
}
